import {Action, Module, Mutation, VuexModule} from "vuex-module-decorators";
import _ from "lodash";
import {clientApi} from "@/http/ClientApi";
import {store} from "@returns-centre/store";
import localStorage from "local-storage-fallback";
import {alertService} from "@returns-centre/services";
import {globals} from "@returns-centre/globals";
import Validator from "validatorjs";

const initialState: TradeInInitialState = {
    trade_in_session: null,
    trade_in: null,
    trade_in_shipping: null,
    trade_in_items: [],
    trade_in_price_deductions: [],
    trade_in_address: null,
    trade_in_notification_email: "",
    return_carrier_services: [],
    selected_return_carrier_service: null,
};

@Module({name: "tradeInModule", namespaced: true})
export default class TradeInModule extends VuexModule {
    errors: ValidationErrors = {};
    trade_in_step = 1;
    confirmed_trade_in: TradeIn | null = null;
    trade_in_session: TradeInSession | null = null;
    trade_in: TradeIn | null = null;
    trade_in_shipping: TradeInShipping | null = null;
    trade_in_items: OrderLineItem[] = [];
    trade_in_price_deductions: TradeInPriceDeduction[] = [];
    trade_in_address: ShopifyAddress | null = null;
    trade_in_notification_email = "";
    categories: Category[] | null = null;
    product_options: ProductOption[] = [];
    is_fetching_categories = false;
    is_fetching_product_options = false;
    is_trade_in_creating = false;
    is_action_processing = false;
    trade_in_has_address_update = false;
    ls_trade_in_session_key = "trade_in_session";
    return_carrier_services: ReturnCarrierService[] = [];
    selected_return_carrier_service: ReturnCarrierService | null = null;
    old_selected_return_carrier_service: ReturnCarrierService | null = null;
    has_agreed_to_terms_and_conditions = false;
    trade_in_step_disabled = false;
    trade_in_steps: TradeInSteps = {
        1: "createTradeInOrderItems",
        2: "createTradeInProduct",
        3: "createTradeIn",
        4: "processTradeInShipping",
        5: "processTradeInConfirmation",
        6: "tradeInThankYou"
    };
    trade_in_step_widths: TradeInStepWidths = {
        1: "100%",
        2: "100%",
        3: "38rem",
        4: "38rem",
        5: "100%",
        6: "38rem"
    };
    trade_in_step_labels: TradeInStepLabels = {
        1: "general.next_step_button",
        2: "general.skip_step_button",
        3: "general.next_step_button",
        4: "general.next_step_button",
        5: "review_trade_in_page.confirm_trade_in_button",
        6: "confirmation_page.continue_shopping_label_button"
    };
    trade_in_step_validation_rules: TradeInStepValidationRules = {
        4: {
            return_carrier_service: "required"
        },
        5: {
            agreed_to_terms_and_conditions: "required"
        }
    };
    validation_errors: ValidationErrors | null = null;
    selected_order_line_items: { [key: string]: OrderLineItem } = {};
    registered_trade_in_products: TradeInProductRecord[] = [];

    get hasTradeInItemsLimitReached() {
        return(tradeInItemsLimit: number): boolean => {

            if(!tradeInItemsLimit) {
                return false;
            }

            return (
                this.selectedOrderLineItemsCount
                    + this.registeredTradeInProductsCount
            ) >= tradeInItemsLimit;
        };
    }

    get tradeInSelectedItemsCount(): number {
        return this.selectedOrderLineItemsCount
        + this.registeredTradeInProductsCount;
    }

    get hasTradeInStepDisabled() {
        return this.trade_in_step_disabled;
    }

    get getCategories() {
        return this.categories;
    }

    get getProductOptions() {
        return this.product_options;
    }

    get validationErrors() {
        return this.validation_errors || null;
    }

    get selectedOrderLineItems() {
        return this.selected_order_line_items;
    }

    get registeredTradeInProducts() {
        return this.registered_trade_in_products;
    }

    get selectedOrderLineItemsCount() {
        if (!this.selected_order_line_items) {
            return 0;
        }
        return Object.keys(this.selected_order_line_items).length;
    }

    get registeredTradeInProductsCount() {
        if (!this.registered_trade_in_products) {
            return 0;
        }
        return Object.keys(this.registered_trade_in_products).length;
    }

    get hasSelectedOrderLineItems() {
        return !_.isEmpty(this.selected_order_line_items);
    }

    get hasRegisteredTradeInProducts() {
        return !_.isEmpty(this.registered_trade_in_products);
    }

    get tradeInStepValidationRules() {
        return this.trade_in_step_validation_rules;
    }

    get hasErrors() {
        return !_.isEmpty(this.errors);
    }

    get getErrors() {
        return this.errors;
    }

    get hasTradeInSession() {
        return _.isEmpty(this.trade_in_session);
    }

    get tradeInStep() {
        return this.trade_in_step;
    }

    get hasTradeInStep() {
        return this.trade_in_step > 1;
    }

    get tradeInMaxSteps() {
        return _.size(this.trade_in_steps);
    }

    get tradeInSteps(): object {
        return this.trade_in_steps;
    }

    get tradeInStepWidths() {
        return this.trade_in_step_widths;
    }

    get tradeInStepLabels() {
        return this.trade_in_step_labels;
    }

    get getTradeInStepActionName() {
        return (step: number): string =>
            this.trade_in_steps[step as keyof TradeInSteps];
    }

    get hasTradeInPriceDeductions() {
        return this.trade_in_price_deductions.length > 0;
    }

    get hasError() {
        return (key: string) => _.hasIn(this.errors, key);
    }

    get getError() {
        return (key: string) => _.hasIn(this.errors, key) ? this.errors[key][0] : "";
    }

    get isTradeInCreating() {
        return this.is_trade_in_creating;
    }

    get isActionProcessing() {
        return this.is_action_processing;
    }

    get hasTradeInCreated() {
        return !!(this.trade_in && this.trade_in.id);
    }

    get tradeInItems() {
        let items: | OrderLineItem[] | TradeInProductRecord[] | TradeInItem[] = [];
        if (!_.isEmpty(this.confirmed_trade_in)) {
            items = this.confirmed_trade_in!.items;
        }
        if (!_.isEmpty(this.trade_in_items)) {
            items = this.trade_in_items;
        }
        return items;
    }

    get totalTradeInQty() {
        if (
            !_.isEmpty(this.selected_order_line_items) &&
            !_.isEmpty(this.registered_trade_in_products)
        ) {
            return (
                Object.keys(this.selected_order_line_items).length +
                Object.keys(this.registered_trade_in_products).length
            );
        }

        if (!_.isEmpty(this.selected_order_line_items)) {
            return Object.keys(this.selected_order_line_items).length;
        }

        if (!_.isEmpty(this.registered_trade_in_products)) {
            return Object.keys(this.registered_trade_in_products).length;
        }

        let sum = 0;
        let items: OrderLineItem[] | TradeInItem[] = [];

        if (!_.isEmpty(this.trade_in_items)) {
            items = this.trade_in_items;
        }

        if (!_.isEmpty(this.confirmed_trade_in)) {
            items = this.confirmed_trade_in!.items;
        }

        for (const row of items) {
            sum += row.selected_quantity;
        }

        return sum;
    }

    get totalTradeInPrice(): string {
        let total = 0;

        // this block is for initial singly quantity based product selection - remains valid until browser is refreshed
        if (
            !_.isEmpty(this.selected_order_line_items) &&
            !_.isEmpty(this.registered_trade_in_products)
        ) {
            for (const key in this.selected_order_line_items) {
                total += _.toNumber(this.selected_order_line_items[key].trade_in_offer.trade_in_offer_value);
            }
            total += this.registered_trade_in_products.reduce(
                (accumulator, obj) => accumulator
                    + ( obj.reward_type === 'store-credit' ?  obj.credit_offered : 0),
                0
            );
        } else if (!_.isEmpty(this.selected_order_line_items)) {
            for (const key in this.selected_order_line_items) {
                total += _.toNumber(this.selected_order_line_items[key].trade_in_offer.trade_in_offer_value);
            }
        } else if (!_.isEmpty(this.registered_trade_in_products)) {
            total = this.registered_trade_in_products.reduce(
                (accumulator, obj) => accumulator
                    + ( obj.reward_type === 'store-credit' ?  obj.credit_offered : 0),
                0
            );
        } else if (!_.isEmpty(this.trade_in_items)) {
            // normal process - once unconfirmed trade is created and browser is refreshed
            total = _.sumBy(this.trade_in_items, function(tradeInItem: OrderLineItem) {
                return tradeInItem.trade_in_offer.trade_in_offer_value * tradeInItem.selected_quantity;
            });
        } else if (!_.isEmpty(this.confirmed_trade_in)) {
            total = _.sumBy(this.confirmed_trade_in!.items, function(tradeInItem: TradeInItem) {
                return tradeInItem.price * tradeInItem.selected_quantity;
            });
        }

        return total.toFixed(2);
    }

    get tradeInDeductions() {
        let deductions: TradeInPriceDeduction[] = [];

        if (this.trade_in && this.trade_in_price_deductions.length) {
            deductions = this.trade_in_price_deductions;
        }

        if (this.confirmed_trade_in) {
            deductions = this.confirmed_trade_in!.deductions;
        }

        return deductions;
    }

    get hasTradeInDeductions() {
        return this.tradeInDeductions.length > 0;
    }

    get totalTradeInDeduction(): string {
        return _.sumBy(this.tradeInDeductions, function(deduction: TradeInPriceDeduction) {
            return deduction.price;
        }).toFixed(2);
    }

    get tradeInNetPrice(): string {
        return (parseFloat(this.totalTradeInPrice) - parseFloat(this.totalTradeInDeduction)).toFixed(2);
    }

    get hasMinimumTradeInPriceMet() {
        if (!store.state.shopModule.shop) {
            return false;
        }
        const minTradeInPrice: number = store.state.shopModule.shop.minimum_trade_in_price;
        const totalTradeInPrice: number = parseFloat(this.totalTradeInPrice);

        return totalTradeInPrice >= minTradeInPrice;
    }

    get hasSelectedTradeInItem() {
        return (tradeInItem: OrderLineItem) => {
            const self = this;
            const item = _.find(self.trade_in_items, {id: tradeInItem.id});
            return !!item;
        };
    }

    get getSelectedTradeInItemQuantity() {
        return (tradeInItem: OrderLineItem) => {
            const self = this;
            const item = _.find(self.trade_in_items, {id: tradeInItem.id});
            return item ? item.selected_quantity : 0;
        };
    }

    get getTradeInItemBorderVariant() {
        return (tradeInItem: OrderLineItem) => {
            const self = this;
            const item = _.find(self.trade_in_items, {id: tradeInItem.id});
            return item ? "success" : "";
        };
    }

    get getOrderLineItemItemQuantityOptions() {
        return (tradeInItem: OrderLineItem) => {
            let options: number[] = [];
            const generateTradeInItemQuantityOptions = (qty: number) => {
                const options = [];
                for (let x = 1; x <= qty; x++) {
                    options.push(x);
                }
                return options;
            };
            const self = this;
            const item = _.find(self.trade_in_items, {id: tradeInItem.id});
            if (!item) {
                options = generateTradeInItemQuantityOptions(tradeInItem.trade_in_available_quantity);
            }

            if (this.trade_in && this.trade_in.status.slug == "trade-in-not-confirmed") {
                return generateTradeInItemQuantityOptions(tradeInItem.quantity);
            }

            if (
                item
                && item.trade_in_quantity
                && item.trade_in_quantity
                === tradeInItem.trade_in_available_quantity
            ) {
                options = [0];
            }

            if (
                item
                && item.trade_in_quantity
                && item.trade_in_quantity < tradeInItem.trade_in_available_quantity
            ) {
                options = generateTradeInItemQuantityOptions(
                    tradeInItem.trade_in_available_quantity - tradeInItem.trade_in_quantity
                );
            }
            return options;
        };
    }

    get hasReturnCarrierServiceSelected() {
        return (returnCarrierService: ReturnCarrierService) => {
            if (!this.selected_return_carrier_service) {
                return false;
            }
            return this.selected_return_carrier_service.id
                === returnCarrierService.id;
        };
    }

    get hasTradeInAddressUpdate() {
        return this.trade_in_has_address_update;
    }

    get hasTradeInAddress() {
        return !_.isEmpty(this.trade_in_address);
    }

    get tradeInAddress() {
        if (!_.isEmpty(this.trade_in_address)) {
            return this.trade_in_address!;
        }

        let address: ShopifyAddress | null = null;
        if (globals.shopify_customer.id) {
            address = {
                ...globals.shopify_customer.address,
                email: globals.shopify_customer.email!
            } as ShopifyAddress;
        }
        if (!globals.logged_in_customer_id && this.trade_in) {
            address = {
                ...this.trade_in.customer.default_address,
                email: store.state.customerModule.orders[0].email!
            } as ShopifyAddress;
        }
        return address;
    }

    get isFetchingCategories() {
        return this.is_fetching_categories;
    }

    get isFetchingProductOptions() {
        return this.is_fetching_product_options;
    }

    get tradeInItemsContainItemExchangeProduct() {
        return (tradeIn: TradeIn): boolean => {
            if (!tradeIn || !tradeIn.items.length) {
                return false;
            }
            let containsItemExchangeProduct: boolean = false;
            for (let tradeInItem of tradeIn.items) {
                if (tradeInItem.trade_in_product_record
                    && tradeInItem.trade_in_product_record.reward_type == 'item-exchange'
                    && tradeInItem.trade_in_product_record.variant) {
                    containsItemExchangeProduct = true;
                    break;
                }
            }
            return containsItemExchangeProduct;
        };
    }

    get tradeInItemsContainStoreCreditProduct() {
        return (tradeIn: TradeIn): boolean => {

            if(!tradeIn || !tradeIn.items.length) {
                return !_.isEmpty(this.selected_order_line_items);
            }

            let containsStoreCreditProduct: boolean = false;
            for(let tradeInItem of tradeIn!.items) {
                if(!tradeInItem.trade_in_product_record) {
                    containsStoreCreditProduct = true;
                    break;
                }
                if(tradeInItem.trade_in_product_record
                    && tradeInItem.trade_in_product_record.reward_type == 'store-credit') {
                    containsStoreCreditProduct = true;
                    break;
                }
            }
            return containsStoreCreditProduct;
        };
    }

    @Action
    setTradeInStepDisabled(val: boolean) {
        this.context.commit('SET_TRADE_IN_STEP_DISABLED', val);
    }

    // @Action
    // async fetchProductOptions(category: Category) {
    //     this.context.commit("SET_IS_FETCHING_PRODUCT_OPTIONS", true);

    //     try {
    //         const response = clientApi.getProductOptions(category);
    //         this.context.commit("SET_PRODUCT_OPTIONS", response);
    //         this.context.commit("SET_IS_FETCHING_PRODUCT_OPTIONS", false);
    //     } catch (e) {
    //         console.log(e);
    //         this.context.commit("SET_ERRORS", e.response.data.errors);
    //         this.context.commit("SET_IS_FETCHING_PRODUCT_OPTIONS", false);
    //     }
    // }

    @Action
    async fetchCategories() {
        this.context.commit("SET_IS_FETCHING_CATEGORIES", true);
        try {
            const response = await clientApi.getCategories();
            this.context.commit("SET_CATEGORIES", response.data);
            this.context.commit("SET_IS_FETCHING_CATEGORIES", false);
        } catch (e) {
            console.log(e);
            this.context.commit("SET_ERRORS", e.response.data.errors);
            this.context.commit("SET_IS_FETCHING_CATEGORIES", false);
        }
    }

    @Action
    setSelectedOrderLineItems(data: SelectedOrderLineItems) {
        this.context.commit("SET_SELECTED_ORDER_LINE_ITEMS", data);
    }

    @Action
    setSelectedRegisteredTradeInProducts(data: RegisteredTradeInProducts) {
        this.context.commit("SET_SELECTED_REGISTERED_TRADE_IN_PRODUCTS", data);
    }

    @Action
    setValidationError(error: { key: string; text: string }) {
        this.context.commit("SET_VALIDATION_ERRORS", {
            [error.key]: [error.text]
        });
    }

    @Action({commit: "SET_ERRORS"})
    setErrors(errors: ValidationErrors) {
        return errors;
    }

    @Action({commit: "RESET"})
    reset() {
    }

    @Action({commit: "RESET_LOCAL_STORAGE"})
    resetLocalStorage() {
    }

    @Action({commit: "RESET_ERRORS"})
    resetErrors() {
    }

    @Action
    setTradeInStep(step: number) {
        this.context.commit("SET_TRADE_IN_STEP", step);
        if (step != _.size(this.trade_in_steps)) {
            this.context.commit("SET_LOCAL_STORAGE");
        }
    }

    @Action({commit: "SET_RETURN_CARRIER_SERVICES"})
    setReturnCarrierServices(returnCarrierServices: ReturnCarrierService[]) {
        return returnCarrierServices;
    }

    @Action({commit: "SET_HAS_AGREED_TERMS_AND_CONDITIONS"})
    setHasAgreedTermsAndConditions(val: boolean) {
        return val;
    }

    @Action({commit: "SET_TRADE_IN_NOTIFICATION_EMAIL"})
    setTradeInNotificationEmail(email = "") {
        return email;
    }

    @Action
    setTradeInAddress(address: ShopifyAddress) {
        this.context.commit("SET_TRADE_IN_ADDRESS", address);
    }

    @Action
    setSelectedReturnCarrierService(returnCarrierService: ReturnCarrierService) {
        this.context.commit(
            "SET_SELECTED_RETURN_CARRIER_SERVICE",
            returnCarrierService
        );
        this.context.commit("SET_VALIDATION_ERRORS");
    }

    @Action
    tradeInValidate() {

        const tradeInStep = this.tradeInStep;
        const rules = _.clone(this.tradeInStepValidationRules[tradeInStep]);
        let customeMessages = {};

        let data: TradeInStepValidationRulesData = {
            first_name: "",
            last_name: "",
            address1: "",
            city: "",
            country: "",
            zip: "",
            email: "",
            province: "",
            trade_in_notification_email: "",
            return_carrier_service: null,
        };

        this.context.commit("SET_VALIDATION_ERRORS", null);

        if (tradeInStep == 3) {
            if (!this.trade_in_address) {
                return {
                    trade_in_address: ["Trade-in address not found"]
                };
            }

            data = {
                first_name: this.trade_in_address["first_name"],
                last_name: this.trade_in_address["last_name"],
                address1: this.trade_in_address["address1"],
                address2: this.trade_in_address["address2"] || "",
                city: this.trade_in_address["city"],
                country: this.trade_in_address["country"],
                zip: this.trade_in_address["zip"],
                trade_in_notification_email: this.trade_in_address["email"],
                email: this.trade_in_address["email"] || "",
                return_carrier_service: this.selected_return_carrier_service,
                province: this.trade_in_address['province']
            };

        }

        if (tradeInStep == 4) {
            data = {
                return_carrier_service: this.selected_return_carrier_service
            };

            customeMessages = {
                "required.return_carrier_service": "Please select a shipping option to continue."
            };
        }

        if (tradeInStep == 5) {
            data = {
                agreed_to_terms_and_conditions: this.has_agreed_to_terms_and_conditions
                    ? this.has_agreed_to_terms_and_conditions
                    : null
            };

            customeMessages = {
                "required.agreed_to_terms_and_conditions": "You must agree to terms and conditions."
            };
        }

        const validation = new Validator(data, rules, customeMessages);
        if (validation.fails()) {
            this.context.commit("SET_VALIDATION_ERRORS", validation.errors.errors);
            return;
        }
    }

    @Action
    async processTradeInShipping() {
        this.context.commit("RESET_ERRORS");
        this.context.commit("SET_IS_ACTION_PROCESSING", true);
        this.context.commit("SET_TRADE_IN_SHIPPING");
        this.context.commit("SET_LOCAL_STORAGE");
        this.context.commit("SET_IS_ACTION_PROCESSING", false);
        return true;
    }

    @Action({commit: "SET_TRADE_IN_ITEMS"})
    setTradeInItems(tradeInItems: OrderLineItem[]) {
        return tradeInItems;
    }

    @Action({commit: "SET_TRADE_IN_PRICE_DEDUCTION"})
    setTradeInPriceDeduction(deduction: TradeInPriceDeduction) {
        return deduction;
    }

    @Action
    async processTradeInStepAction(step: number): Promise<boolean> {
        return await this.context.dispatch(this.trade_in_steps[step as keyof TradeInSteps]);
    }

    @Action
    async processTradeInConfirmation() {
        if (!this.trade_in) {
            this.context.commit("SET_ERRORS", {
                trade_in_not_found: [
                    "An unknown error occurred. Please refresh the page and try again"
                ]
            });
            return false;
        }

        this.context.commit("RESET_ERRORS");
        this.context.commit("SET_IS_ACTION_PROCESSING", true);

        try {
            const response = await clientApi.confirmTradeIn(this.trade_in.id, {
                trade_in_id: this.trade_in.id,
                address: this.trade_in_address,
                notification_email: this.trade_in_address?.email! || "",
                trade_in_deductions: this.trade_in_price_deductions,
                return_carrier_service: this.selected_return_carrier_service,
                trade_in_items: this.trade_in.items,
                shop_domain: store.state.shopModule.shop.shop_domain,
                customer_id: store.state.customerModule.orders.length
                    ? store.state.customerModule.orders[0].customer_id
                    : 0,
                shopify_customer_id: store.state.customerModule.returns_portal_shopify_customer.id || 0
            } as ConfirmTradeInRequest);

            if(_.hasIn(response.data, 'invoice_url')) {
                localStorage.removeItem(this.ls_trade_in_session_key);
                (document.getElementsByClassName('TRC-App')[0] as HTMLDivElement).style.visibility = 'hidden';
                window.location.href = response.data.invoice_url!;
            } else {
                this.context.commit("SET_CONFIRMED_TRADE_IN", response.data);
                this.context.commit("SET_LOCAL_STORAGE");
                this.context.commit("CONFIRM_TRADE_IN_SESSION");
                alertService.showMessage(store.state.shopModule.shop.return_centre_translations['general']['alert_messages']['success']);
            }

            return true;
        } catch (e) {
            console.error(e);
            alertService.showError(store.state.shopModule.shop.return_centre_translations['general']['alert_messages']['failure']);
            this.context.commit("SET_ERRORS", e.response.data.errors);
            throw new Error(e);
        } finally {
            this.context.commit("SET_IS_ACTION_PROCESSING", false);
        }
    }

    @Action
    async createTradeInProduct() {
        this.context.commit("SET_IS_ACTION_PROCESSING", true);
        this.context.commit(
            "SET_SELECTED_REGISTERED_TRADE_IN_PRODUCTS",
            this.registered_trade_in_products
        );
        this.context.commit("SET_LOCAL_STORAGE");
        this.context.commit("SET_IS_ACTION_PROCESSING", false);

        return true;
    }

    @Action
    async registerTradeInProduct(product: TradeInProductRecord) {
        this.context.commit("SET_IS_ACTION_PROCESSING", true);

        this.registered_trade_in_products.push({...product});
        this.context.commit(
            "SET_SELECTED_REGISTERED_TRADE_IN_PRODUCTS",
            this.registered_trade_in_products
        );
        this.context.commit("SET_LOCAL_STORAGE");
        this.context.commit("SET_IS_ACTION_PROCESSING", false);

        return true;
    }

    @Action
    async updateTradeInProductRecord(product: TradeInProductRecord) {
        this.context.commit("SET_IS_ACTION_PROCESSING", true);

        _.remove(
            this.registered_trade_in_products,
            (item: TradeInProductRecord) => item.uuid === product.uuid
        );

        product.product_answers = _.sortBy(product.product_answers!, ['sort_order']);

        this.registered_trade_in_products.push({...product});

        this.context.commit(
            "SET_SELECTED_REGISTERED_TRADE_IN_PRODUCTS",
            this.registered_trade_in_products
        );
        this.context.commit("SET_LOCAL_STORAGE");
        this.context.commit("SET_IS_ACTION_PROCESSING", false);

        return true;
    }

    @Action
    async removeTradeInProduct(product: TradeInProductRecord) {
        let index = this.registered_trade_in_products.indexOf(product);
        this.registered_trade_in_products.splice(index, 1);

        return true;
    }

    @Action
    async removeTradeInOrderItem(index: string) {
        const selectedOrderItems = {...this.selected_order_line_items};
        delete selectedOrderItems[index];
        this.context.commit("SET_SELECTED_ORDER_LINE_ITEMS", selectedOrderItems);

        return true;
    }

    @Action
    async createTradeInOrderItems() {
        this.context.commit("SET_IS_ACTION_PROCESSING", true);

        // rebuild trade_in_items request structure
        //TODO -- come up with better implementation
        const orderLineItemsGrouped = _.countBy(
            _.map(this.selected_order_line_items, function(row, key) {
                return _.toNumber(key.split("_")[0]);
            }),
            (v) => v
        );
        const selectedOrderLineItems: OrderLineItem[] = [];
        for (const orderLineItemId in orderLineItemsGrouped) {
            for (const orderLineItemKey in this.selected_order_line_items) {
                if (orderLineItemKey.includes(orderLineItemId)) {
                    selectedOrderLineItems.push({
                        ...this.selected_order_line_items[orderLineItemKey],
                        selected_quantity:
                            orderLineItemsGrouped[orderLineItemId]
                    });
                    break;
                }
            }
        }
        this.context.commit("SET_TRADE_IN_ITEMS", selectedOrderLineItems);
        this.context.commit("SET_LOCAL_STORAGE");
        this.context.commit("SET_IS_ACTION_PROCESSING", false);

        return true;
    }

    @Action
    async createTradeIn() {
        if (!_.isEmpty(this.errors)) {
            return false;
        }
        this.context.commit("SET_IS_ACTION_PROCESSING", true);
        try {
            const tradeIn = await clientApi.createTradeIn({
                trade_in: this.trade_in ?? undefined,
                trade_in_items: this.trade_in_items,
                trade_in_products: this.registered_trade_in_products,
                customer: this.context.rootState.customerModule.returns_portal_shopify_customer
                    ?? this.trade_in_address,
                address: this.trade_in_address
            });

            this.context.commit("SET_TRADE_IN", tradeIn);
            return true;
        } catch (e) {alertService.showError("Error occurred.");
            this.context.commit("SET_ERRORS", e.response.data.errors);
            return false;
        } finally {
            this.context.commit("SET_IS_ACTION_PROCESSING", false);
        }
    }

    @Action({commit: "SELECT_TRADE_IN_ITEM"})
    selectTradeInItem(tradeInItem: OrderLineItem) {
        return tradeInItem;
    }

    @Action({commit: "REMOVE_TRADE_IN_ITEM"})
    removeTradeInItem(tradeInItem: OrderLineItem) {
        return tradeInItem;
    }

    @Action({commit: "REMOVE_ALL_TRADE_IN_ITEMS"})
    removeAllTradeInItems() {
    }

    @Action
    finishTradeIn() {
        localStorage.removeItem(this.ls_trade_in_session_key);
    }

    @Mutation
    SET_TRADE_IN_STEP_DISABLED(val: boolean) {
        this.trade_in_step_disabled = val;
    }

    @Mutation
    SET_TRADE_IN_ITEMS(tradeInItems: OrderLineItem[]) {
        this.trade_in_items = tradeInItems;
    }

    @Mutation
    SET_RETURN_CARRIER_SERVICES(carrierServices: ReturnCarrierService[]) {
        this.return_carrier_services = carrierServices;
    }

    @Mutation
    SET_IS_TRADE_IN_CREATING(val: boolean) {
        this.is_trade_in_creating = val;
    }

    @Mutation
    SET_IS_ACTION_PROCESSING(val: boolean) {
        this.is_action_processing = val;
    }

    @Mutation
    SET_TRADE_IN(tradeIn: TradeIn) {
        this.trade_in = tradeIn;
    }

    @Mutation
    SELECT_TRADE_IN_ITEM(tradeInItem: OrderLineItem) {
        this.trade_in_items.push(tradeInItem);
    }

    @Mutation
    REMOVE_TRADE_IN_ITEM(tradeInItem: OrderLineItem) {
        this.trade_in_items = _.filter(this.trade_in_items, function(item) {
            return tradeInItem.id !== item.id;
        });
    }

    @Mutation
    REMOVE_ALL_TRADE_IN_ITEMS() {
        this.trade_in_items = [];
    }

    @Mutation
    SET_SELECTED_RETURN_CARRIER_SERVICE(
        returnCarrierService: ReturnCarrierService
    ) {
        this.selected_return_carrier_service = returnCarrierService;
        this.old_selected_return_carrier_service = returnCarrierService;
    }

    @Mutation
    SET_TRADE_IN_PRICE_DEDUCTION(deduction: TradeInPriceDeduction) {
        _.remove(this.trade_in_price_deductions, function(obj) {
            return obj.deduction_type.slug === deduction.deduction_type.slug;
        });
        this.trade_in_price_deductions.push(deduction);
    }

    @Mutation
    RESET_TRADE_IN_PRICE_DEDUCTION() {
        this.trade_in_price_deductions = [];
    }

    @Mutation
    SET_TRADE_IN_SHIPPING() {
        this.trade_in_shipping = {
            trade_in_address: this.trade_in_address,
            trade_in_notification_email: this.trade_in_notification_email,
            trade_in_selected_return_carrier_service: this.selected_return_carrier_service,
        };
    }

    @Mutation
    RESET_LOCAL_STORAGE() {
        localStorage.removeItem(this.ls_trade_in_session_key);
    }

    @Mutation
    CONFIRM_TRADE_IN_SESSION() {
        localStorage.removeItem(this.ls_trade_in_session_key);
        store.state.customerModule.orders = [];
        if (this.confirmed_trade_in) {
            localStorage.setItem(
                this.ls_trade_in_session_key,
                JSON.stringify({
                    trade_in_step: _.size(this.trade_in_steps),
                    confirmed_trade_in: this.confirmed_trade_in
                })
            );
        }

        Object.assign(this, initialState);
    }

    @Mutation
    SET_LOCAL_STORAGE() {
        localStorage.setItem(
            this.ls_trade_in_session_key,
            JSON.stringify({
                trade_in_step: this.trade_in_step,
                trade_in: this.trade_in,
                confirmed_trade_in: this.confirmed_trade_in,
                trade_in_return_carrier_services: this.return_carrier_services,
                trade_in_items: this.trade_in_items,
                trade_in_address: this.trade_in_address,
                trade_in_customer:
                store.state.customerModule.returns_portal_shopify_customer,
                trade_in_customer_orders: store.state.customerModule.orders,
                trade_in_products: this.registered_trade_in_products,
                trade_in_shipping: this.trade_in_shipping,
                trade_in_price_deductions: this.trade_in_price_deductions
            })
        );
    }

    @Mutation
    SET_TRADE_IN_ADDRESS(address: ShopifyAddress) {
        this.trade_in_address = address;
        this.trade_in_notification_email = address.email || "";
    }

    @Mutation
    SET_TRADE_IN_NOTIFICATION_EMAIL(email: string) {
        this.trade_in_notification_email = email;
        this.trade_in_address!.email = email;
    }

    @Mutation
    SET_HAS_AGREED_TERMS_AND_CONDITIONS(val: boolean) {
        this.has_agreed_to_terms_and_conditions = val;
    }

    @Mutation
    SET_CONFIRMED_TRADE_IN(tradeIn: TradeIn) {
        this.confirmed_trade_in = tradeIn;
    }

    @Mutation
    SET_TRADE_IN_STEP(step: number) {
        this.trade_in_step = step;
    }

    @Mutation
    SET_ERRORS(errors: ValidationErrors) {
        this.errors = errors;
    }

    @Mutation
    CLEAR_ERROR(field: string) {
        if (this.errors && this.errors[field]) {
            delete this.errors[field];
        }
    }

    @Mutation
    CLEAR_VALIDATION_ERROR(field: string) {
        if (this.validation_errors && this.validation_errors[field]) {
            delete this.validation_errors[field];
        }
    }

    @Mutation
    RESET_ERRORS() {
        this.errors = {};
        this.validation_errors = null;
    }

    @Mutation
    RESET() {
        this.errors = {};
        this.validation_errors = null;
        store.state.returnCarrierServiceModule.return_carrier_services = [];
        this.selected_return_carrier_service = null;
        this.has_agreed_to_terms_and_conditions = false;
    }

    @Mutation
    SET_SELECTED_ORDER_LINE_ITEMS(data: SelectedOrderLineItems) {
        this.selected_order_line_items = data;
    }

    @Mutation
    SET_SELECTED_REGISTERED_TRADE_IN_PRODUCTS(
        data: TradeInProductRecord[]
    ) {
        this.registered_trade_in_products = data;
    }

    @Mutation
    SET_VALIDATION_ERRORS(errors: ValidationErrors | null = null) {
        this.validation_errors = errors;
    }

    @Mutation
    SET_CATEGORIES(categories: Category[]) {
        this.categories = categories;
    }

    @Mutation
    SET_IS_FETCHING_CATEGORIES(val: boolean) {
        this.is_fetching_categories = val;
    }

    @Mutation
    SET_PRODUCT_OPTIONS(product_options: ProductOption[]) {
        this.product_options = product_options;
    }

    @Mutation
    SET_IS_FETCHING_PRODUCT_OPTIONS(val: boolean) {
        this.is_fetching_product_options = val;
    }
}
